import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Inbox, Mail, Phone, Building2, User, RefreshCw, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const heading = { fontFamily: "'Outfit', sans-serif" };

const STATUSES = ['new', 'contacted', 'in_progress', 'converted', 'closed'];
const STATUS_STYLE = {
  new: 'bg-[#C0975C] text-[#0A1F44]',
  contacted: 'bg-[#3E4E8B] text-white',
  in_progress: 'bg-[#0E7C7B] text-white',
  converted: 'bg-[#2E7D5B] text-white',
  closed: 'bg-white/10 text-[#94A9C9]',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

export default function LeadsInbox() {
  const { token } = useAuth();
  const [data, setData] = useState({ leads: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, auth);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const updateStatus = async (id, status) => {
    const prev = data.leads;
    setData((d) => ({ ...d, leads: d.leads.map((l) => (l.id === id ? { ...l, status } : l)) }));
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { status }, auth);
      toast.success('Status updated');
      load();
    } catch (e) {
      setData((d) => ({ ...d, leads: prev }));
      toast.error('Could not update status');
    }
  };

  const filtered = data.leads.filter((l) => {
    if (filter !== 'all' && l.status !== filter) return false;
    if (q) {
      const s = `${l.name} ${l.email} ${l.phone} ${l.message}`.toLowerCase();
      if (!s.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#C0975C] flex items-center justify-center rounded-xl">
              <Inbox className="w-7 h-7 text-[#0A1F44]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={heading}>Leads <span className="text-[#C0975C]">Inbox</span></h1>
              <p className="text-[#94A9C9]">Consultation requests from the website</p>
            </div>
          </div>
          <button onClick={load} data-testid="leads-refresh" className="neo-button-secondary inline-flex items-center gap-2 px-5 py-3">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </motion.div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <button onClick={() => setFilter('all')} className={`neo-card p-4 text-left ${filter === 'all' ? 'border-[#C0975C]' : ''}`} data-testid="filter-all">
            <p className="text-2xl font-bold text-white" style={heading}>{data.total}</p>
            <p className="text-xs uppercase tracking-wider text-[#94A9C9]">All</p>
          </button>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`neo-card p-4 text-left ${filter === s ? 'border-[#C0975C]' : ''}`} data-testid={`filter-${s}`}>
              <p className="text-2xl font-bold text-[#C0975C]" style={heading}>{data.counts[s] || 0}</p>
              <p className="text-xs uppercase tracking-wider text-[#94A9C9] capitalize">{s.replace('_', ' ')}</p>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-5 flex items-center gap-2 bg-[#122C5A] border border-[#1D3D72] rounded-xl px-4 py-3 max-w-md">
          <Search className="w-4 h-4 text-[#C0975C]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone or message"
            className="w-full bg-transparent text-white outline-none text-sm"
            data-testid="leads-search"
          />
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#C0975C] animate-pulse">Loading leads...</div>
        ) : filtered.length === 0 ? (
          <div className="neo-card p-12 text-center">
            <Mail className="w-10 h-10 mx-auto mb-3 text-[#C0975C]" />
            <p className="text-[#94A9C9]">No leads to show yet.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="leads-list">
            {filtered.map((l, i) => (
              <motion.div
                key={l.id || i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="neo-card p-5 lg:p-6"
                data-testid="lead-card"
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-bold text-white" style={heading}>{l.name || 'Unnamed lead'}</h3>
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${l.segment === 'corporate' ? 'bg-[#1D3D72] text-white' : 'bg-white/5 text-[#94A9C9]'}`}>
                        {l.segment === 'corporate' ? <Building2 className="w-3 h-3" /> : <User className="w-3 h-3" />}
                        {l.segment === 'corporate' ? 'Business' : 'Individual'}
                      </span>
                      <span className="text-xs text-[#94A9C9]">{fmtDate(l.created_at)}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#c6d0e0]">
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-2 hover:text-[#C0975C]"><Mail className="w-4 h-4 text-[#C0975C]" />{l.email}</a>
                      <a href={`tel:${l.phone}`} className="inline-flex items-center gap-2 hover:text-[#C0975C]"><Phone className="w-4 h-4 text-[#C0975C]" />{l.phone}</a>
                    </div>
                    {l.message && <p className="mt-3 text-sm text-[#94A9C9] bg-[#061530] rounded-lg p-3 border border-[#1D3D72]">{l.message}</p>}
                  </div>
                  <div className="lg:w-48 flex-shrink-0">
                    <label className="block text-[11px] uppercase tracking-wider text-[#94A9C9] mb-1.5">Status</label>
                    <select
                      value={l.status}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      className={`w-full rounded-lg px-3 py-2.5 text-sm font-semibold cursor-pointer outline-none ${STATUS_STYLE[l.status] || 'bg-white/10 text-white'}`}
                      data-testid={`lead-status-${l.id}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-[#0A1F44] text-white capitalize">{s.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
